import axios from 'axios';
import api from './api';
import { t } from '../i18n';

/**
 * Service für die Turniersoftware ("Turnierbetrieb").
 *
 * Dashboard-Calls laufen über die normale api-Instanz (dc-sign via Interceptor).
 * Board-Calls (Scorer-Gerät) laufen über eine EIGENE axios-Instanz mit
 * `dc-board`-Header — bewusst OHNE den 401→/login-Redirect der Haupt-Instanz,
 * damit ein ungültiges Board-Token nicht die Login-Seite erzwingt.
 */

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

export const BOARD_TOKEN_KEY = 'dc-board-token';

// ---------- Typen ----------

export type OpStatus = 'SETUP' | 'RUNNING' | 'FINISHED';
export type OutMode = 'STRAIGHT' | 'DOUBLE' | 'MASTER';
export type InMode = 'STRAIGHT' | 'DOUBLE';
export type LegsMode = 'BEST_OF' | 'FIRST_TO';
export type BoardStatus = 'FREE' | 'ASSIGNED' | 'LIVE';
export type MatchStatus = 'PENDING' | 'READY' | 'ASSIGNED' | 'LIVE' | 'FINISHED' | 'APPROVED' | 'BYE' | 'VOID';
export type WithdrawMode = 'ANNUL' | 'FORFEIT';
export type StageType = 'KO' | 'GROUP_KO' | 'FACUP';

export interface GameConfig {
  startScore: number;
  outMode: OutMode;
  inMode: InMode;
  legsMode: LegsMode;
  legsCount: number;
}

export interface OpSettings {
  autoAssign?: boolean;
  /** Ergebnisse müssen von der Turnierleitung freigegeben werden, bevor es weitergeht. */
  resultApproval?: boolean;
}

/** Payload des Socket-Events `facupRoundDrawn` (Namen, keine IDs). */
export interface FacupDrawPair {
  a: string;
  b: string;
}

export interface FacupDrawEvent {
  roundNumber: number;
  /** Vom Backend fertig formatiert: 'Runde 2' | 'Halbfinale' | 'Finale' | … */
  roundLabel: string;
  pairs: FacupDrawPair[];
  bye?: string | null;
}

export interface GroupConfig {
  groupSize: number;
  qualifiersPerGroup: number;
}

export interface Operation {
  id: string;
  tournament: string;
  status: OpStatus;
  gameConfig: GameConfig;
  settings?: OpSettings | null;
  /** Turnierformat: direktes KO oder Gruppenphase + KO. Ältere Backends liefern das Feld nicht. */
  stageType?: StageType | null;
  groupConfig?: GroupConfig | null;
  /** Nur bei Competition-Betrieben gesetzt (Subtournament-Operation), sonst null/fehlend. */
  competitionName?: string | null;
  parentTournamentName?: string | null;
  parentTournamentSlug?: string | null;
  /** Sieger (Teilnehmer-Id) nach „Turnier beenden“ — ADDITIV/nullable, ältere Backends liefern das Feld nicht. */
  winnerParticipant?: string | null;
}

export interface Participant {
  id: string;
  name: string;
  entry?: string | null;
  user?: string | null;
  /** Gruppen-Label nach der Auslosung (nur GROUP_KO). */
  group?: string | null;
  /** Öffentlicher Profil-Username (nur wenn Profil öffentlich), sonst null/fehlend. */
  username?: string | null;
  /** Profilbild-Dateiname (über /api/file/<name> auflösen), sonst null/fehlend. */
  image?: string | null;
  /** Gestrichen (Turnierleitung im laufenden Betrieb) — ADDITIV/nullable, ältere Backends liefern die Felder nicht. */
  withdrawn?: boolean | null;
  withdrawnAt?: number | null;
  withdrawMode?: WithdrawMode | null;
  /** Nachgemeldet (nur GROUP_KO) — ADDITIV/nullable. */
  lateEntry?: boolean | null;
}

/**
 * Datei-URL für Uploads (z. B. Turnierplakate der Live-Liste, Teilnehmer-Profilbilder).
 * Robust gegen historische Datenformen:
 * - null/undefined/leer/whitespace → null (Fallback-Rendering)
 * - data:-URI → unverändert durchreichen
 * - volle http(s)-URL → unverändert durchreichen
 * - absoluter Pfad (`/…`) → an API-Base hängen
 * - sonst (nackter Dateiname) → `/api/file/<name>`
 */
export function fileUrl(fileName?: string | null): string | null {
  if (typeof fileName !== 'string') return null;
  const name = fileName.trim();
  if (!name) return null;
  if (name.startsWith('data:')) return name;
  if (name.startsWith('http://') || name.startsWith('https://')) return name;
  if (name.startsWith('/')) return `${API_BASE_URL}${name}`;
  return `${API_BASE_URL}/api/file/${name}`;
}

export interface Board {
  id: string;
  name: string;
  code?: string | null;
  status: BoardStatus;
  currentMatch?: string | null;
  /** Feste Gruppen-Bindung (Gruppen-Label, nur GROUP_KO) — additiv/nullable, ältere Backends liefern das Feld nicht. */
  fixedGroup?: string | null;
  /** Pausiert = bekommt keine Zuweisung — ADDITIV/nullable, ältere Backends liefern das Feld nicht. */
  paused?: boolean | null;
}

export interface Visit {
  playerIdx: number;
  score: number;
  dartsUsed?: number | null;
  remaining: number;
  isBust: boolean;
}

export interface Leg {
  starter: number;
  visits: Visit[];
  winner?: number | null;
  checkoutScore?: number | null;
  checkoutDarts?: number | null;
}

export interface PlayerStats {
  average?: number | null;
  first9Average?: number | null;
  oneEighties?: number | null;
  highFinish?: number | null;
  totalDarts?: number | null;
}

export interface MatchPlayer {
  participant?: string | null;
  /** Nur in Board-/Socket-Payloads gesetzt (buildMatchPayload). */
  name?: string | null;
  /** Öffentlicher Profil-Username — additiv/nullable, ältere Backends liefern das Feld nicht. */
  username?: string | null;
  /** Profilbild-Dateiname (über fileUrl()//api/file/<name> auflösen) — additiv/nullable. */
  image?: string | null;
  legsWon: number;
  stats?: PlayerStats | null;
}

export interface OpMatch {
  id: string;
  round: string;
  /** Gruppen-Label (A, B, ...) bei round === 'GROUP', sonst null. */
  group?: string | null;
  roundNumber: number;
  matchNumber: number;
  nextMatchNumber?: number | null;
  nextSlot?: number | null;
  players: MatchPlayer[];
  legs: Leg[];
  gameConfig: GameConfig;
  status: MatchStatus;
  winner?: number | null;
  board?: string | null;
  firstLegStarter?: number | null;
  /** Participant-Id des Schreibers (Kreider), falls zugewiesen. */
  writer?: string | null;
  /** Anzeigename des Schreibers, falls zugewiesen. */
  writerName?: string | null;
  /** Aktiver Werfer (0|1) — ADDITIV, nur bei Status LIVE gesetzt; ältere Backends liefern das Feld nicht. */
  currentThrower?: number | null;
  /** Restpunkte [Spieler 1, Spieler 2] — ADDITIV, nur bei Status LIVE gesetzt; ältere Backends liefern das Feld nicht. */
  remaining?: number[] | null;
  /** Kampflos entschieden (Streichen) — ADDITIV/nullable. */
  walkover?: boolean | null;
  /** Ergebnis manuell von der Turnierleitung eingetragen — ADDITIV/nullable. */
  manual?: boolean | null;
  /** Grund der Annullierung (nur Status VOID) — ADDITIV/nullable. */
  voidReason?: string | null;
}

/**
 * Gruppen-Vorschau vor der Auslosung (ADDITIV/nullable): nur bei stageType
 * GROUP_KO im Status SETUP. Clients rechnen NICHT selbst (Backend-Regel:
 * Gruppenanzahl = round(n/größe), nie Gruppen < 2 Spieler, Größen ±1).
 */
export interface GroupPreview {
  groupCount: number;
  sizes: number[];
  labels: string[];
  qualifierCount: number;
  koPossible: boolean;
  warnings: string[];
}

export interface GroupStandingRow {
  participant: string;
  played: number;
  wins: number;
  losses: number;
  legsFor: number;
  legsAgainst: number;
  legDiff: number;
  points: number;
  /** Spieler gestrichen — ADDITIV/nullable (grau/durchgestrichen zeichnen). */
  withdrawn?: boolean | null;
}

export interface OpGroup {
  label: string;
  members: string[];
  /** Bereits sortiert (inkl. Tiebreaker) vom Backend geliefert. */
  standings: GroupStandingRow[];
}

export interface OperationData {
  operation: Operation;
  participants: Participant[];
  boards: Board[];
  matches: OpMatch[];
  /** Nur bei stageType GROUP_KO gesetzt, sonst null/undefined. */
  groups?: OpGroup[] | null;
  /** Gruppen-Vorschau (nur GROUP_KO im SETUP) — ADDITIV/nullable, ältere Backends liefern das Feld nicht. */
  groupPreview?: GroupPreview | null;
  /** Nur bei Competition-Betrieben gesetzt (Subtournament-Operation), sonst null/fehlend. */
  competitionName?: string | null;
  parentTournamentName?: string | null;
  parentTournamentSlug?: string | null;
}

export interface VisitResult {
  isBust: boolean;
  isCheckout: boolean;
  legFinished: boolean;
  matchFinished: boolean;
  newRemaining: number;
  playerIdx: number;
}

export interface BoardInfo {
  id: string;
  name: string;
  status: BoardStatus;
}

export interface BoardLoginResponse {
  token: string;
  board: BoardInfo;
  operationId: string;
  gameConfig: GameConfig | null;
}

export interface BoardStateResponse {
  board: BoardInfo;
  operationStatus: OpStatus | null;
  match: OpMatch | null;
}

/** Eintrag der öffentlichen Live-Liste (Startseiten-Section). */
export interface LiveListEntry {
  operationId: string;
  tournamentId: string;
  name: string;
  slug?: string | null;
  image?: string | null;
  stageType?: StageType | null;
  startedAt?: number | null;
  liveMatches: number;
  participants: number;
  /** Nur bei Competition-Betrieben gesetzt (Subtournament-Operation), sonst null/fehlend. */
  competitionName?: string | null;
  parentTournamentName?: string | null;
  parentTournamentSlug?: string | null;
}

/** Eigener, nicht beendeter Turnierbetrieb (GET /api/tournament-op/mine). */
export interface MyOperationEntry {
  operationId: string;
  tournamentId: string;
  name: string;
  status: 'SETUP' | 'RUNNING';
  startedAt?: number | null;
  createdAt?: number | null;
  /** Nur bei Competition-Betrieben gesetzt (Subtournament-Operation), sonst null/fehlend. */
  competitionName?: string | null;
  parentTournamentName?: string | null;
  parentTournamentSlug?: string | null;
}

/** Öffentliches Einzelspiel (Match-Live-View). */
export interface PublicMatchResponse {
  operation: { id: string; status: OpStatus; stageType?: StageType | null };
  match: OpMatch;
  boardName?: string | null;
}

// ---------- KO-Baum + Spielliste (GET /:id/bracket, öffentlich, 27.09.2026) ----------
// Alles kommt fertig aus dem Backend (bracket-view.js): Runden-Labels, Verkettung
// (feeds), Averages. Das Web zeichnet nur.

export interface BracketPlayer {
  idx: number;
  participant: string | null;
  name: string | null;
  legsWon: number;
  average: number | null;
  winner: boolean;
}

export interface BracketMatch {
  id: string;
  matchNumber: number;
  round: string;
  roundNumber: number;
  group: string | null;
  status: MatchStatus;
  winner: number | null;
  walkover: boolean;
  manual: boolean;
  voidReason: string | null;
  board: string | null;
  boardName: string | null;
  nextMatchNumber: number | null;
  nextSlot: number | null;
  /** Matchnummern der Vorrunden-Spiele je Slot: [slot0, slot1] (null = kein Zubringer). */
  feeds: (number | null)[];
  players: BracketPlayer[];
  currentThrower?: number | null;
  remaining?: number[] | null;
}

export interface BracketRound {
  roundNumber: number;
  round: string;
  /** Vom Backend fertig: 'Finale' | 'Halbfinale' | 'Viertelfinale' | 'Letzte 32' | … */
  label: string;
  playersInRound: number;
  matches: BracketMatch[];
}

export interface BracketChampion {
  participant: string | null;
  name: string | null;
}

export interface BracketTree {
  legsLabel: string | null;
  koRounds: BracketRound[];
  facupRounds: BracketRound[];
  champion: BracketChampion | null;
}

export interface BracketListSection {
  key: string;
  label: string;
  matches: BracketMatch[];
}

export interface BracketList {
  live: BracketMatch[];
  /** Finale zuerst, dann die weiteren Runden absteigend, dann Gruppen. */
  sections: BracketListSection[];
  totalAverage: number | null;
}

export interface BracketState {
  operation: { id: string; status: OpStatus; stageType?: StageType | null; winnerParticipant?: string | null };
  bracket: BracketTree;
  list: BracketList;
}

/**
 * Defensives Parsen der Bracket-Antwort (ältere Backends kennen den Endpoint
 * nicht bzw. liefern andere Bodies): fehlende Listen → leer, fehlende Felder → null.
 */
export function parseBracketState(raw: unknown): BracketState | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, any>;
  const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  const parseMatch = (m: any): BracketMatch | null => {
    if (!m || typeof m !== 'object' || typeof m.id !== 'string') return null;
    const feedsRaw = Array.isArray(m.feeds) ? m.feeds : [];
    const players: BracketPlayer[] = (Array.isArray(m.players) ? m.players : []).map((p: any, idx: number) => ({
      idx: typeof p?.idx === 'number' ? p.idx : idx,
      participant: str(p?.participant),
      name: str(p?.name),
      legsWon: num(p?.legsWon) ?? 0,
      average: num(p?.average),
      winner: p?.winner === true,
    }));
    return {
      id: m.id,
      matchNumber: num(m.matchNumber) ?? 0,
      round: str(m.round) ?? '',
      roundNumber: num(m.roundNumber) ?? 0,
      group: str(m.group),
      status: (str(m.status) ?? 'PENDING') as MatchStatus,
      winner: num(m.winner),
      walkover: m.walkover === true,
      manual: m.manual === true,
      voidReason: str(m.voidReason),
      board: str(m.board),
      boardName: str(m.boardName),
      nextMatchNumber: num(m.nextMatchNumber),
      nextSlot: num(m.nextSlot),
      feeds: [num(feedsRaw[0]), num(feedsRaw[1])],
      players,
      currentThrower: num(m.currentThrower),
      remaining: Array.isArray(m.remaining) ? m.remaining.map((x: unknown) => num(x) ?? 0) : null,
    };
  };
  const parseMatches = (list: unknown): BracketMatch[] =>
    (Array.isArray(list) ? list : []).map(parseMatch).filter((m): m is BracketMatch => m !== null);
  const parseRounds = (list: unknown): BracketRound[] =>
    (Array.isArray(list) ? list : []).map((x: any) => ({
      roundNumber: num(x?.roundNumber) ?? 0,
      round: str(x?.round) ?? '',
      label: str(x?.label) ?? '',
      playersInRound: num(x?.playersInRound) ?? 0,
      matches: parseMatches(x?.matches),
    }));
  const op = r.operation && typeof r.operation === 'object' ? r.operation : {};
  const bracket = r.bracket && typeof r.bracket === 'object' ? r.bracket : {};
  const list = r.list && typeof r.list === 'object' ? r.list : {};
  const champion = bracket.champion && typeof bracket.champion === 'object'
    ? { participant: str(bracket.champion.participant), name: str(bracket.champion.name) }
    : null;
  return {
    operation: {
      id: str(op.id) ?? '',
      status: (str(op.status) ?? 'RUNNING') as OpStatus,
      stageType: (str(op.stageType) as StageType | null) ?? null,
      winnerParticipant: str(op.winnerParticipant),
    },
    bracket: {
      legsLabel: str(bracket.legsLabel),
      koRounds: parseRounds(bracket.koRounds),
      facupRounds: parseRounds(bracket.facupRounds),
      champion,
    },
    list: {
      live: parseMatches(list.live),
      sections: (Array.isArray(list.sections) ? list.sections : []).map((s: any) => ({
        key: str(s?.key) ?? '',
        label: str(s?.label) ?? '',
        matches: parseMatches(s?.matches),
      })),
      totalAverage: num(list.totalAverage),
    },
  };
}

// ---------- Labels ----------

const ROUND_LABELS: Record<string, string> = {
  F: 'Finale',
  SF: 'Halbfinale',
  QF: 'Viertelfinale',
  R16: 'Achtelfinale',
};

/**
 * Runden-Label eines Matches — Gruppen-Matches bekommen
 * "Gruppe A · Runde 1", KO-Matches "Finale"/"Halbfinale"/…
 */
export function matchRoundLabel(match: Pick<OpMatch, 'round' | 'group' | 'roundNumber'>): string {
  if (match.round === 'GROUP') {
    return t('web.service.tournament-op-service.gruppe-runde', { value: match.group ?? '?', roundNumber: match.roundNumber });
  }
  // FA-Cup: schlichtes Label — die Auslosungs-Show bekommt ihr eigenes Label vom Backend.
  if (match.round === 'FACUP') return t('web.service.tournament-op-service.runde', { roundNumber: match.roundNumber });
  if (ROUND_LABELS[match.round]) return ROUND_LABELS[match.round];
  if (match.round?.startsWith('R')) return t('web.service.tournament-op-service.runde-der-letzten', { slice: match.round.slice(1) });
  return match.round;
}

/** Sprechende KO-Rundennamen nach Distanz zum Finale (Index 0 = Finale). */
const KO_STAGE_BY_DISTANCE = [t('web.page.world-cup-of-darts-page.finale'), t('web.component.user-predictions-modal.halbfinale'), t('web.page.world-cup-of-darts-page.viertelfinale'), t('web.page.world-cup-of-darts-page.achtelfinale')];

/**
 * Einheitliches Stage-Label eines Matches (Web + Flutter identisch):
 * - Gruppenspiele: "Gruppe A · Spiel 3" (laufende Matchnummer statt Rundenzahl)
 * - KO-/FA-Cup-Spiele: "Finale"/"Halbfinale"/"Viertelfinale"/"Achtelfinale" anhand
 *   der Distanz zur höchsten Rundennummer der Bracket-Matches der Operation;
 *   weiter entfernte Runden → bisheriges Label (matchRoundLabel).
 *
 * Defensiv: ohne roundNumber/allMatches greift das bisherige Verhalten. Eine
 * Plausibilitätsprüfung (Runde mit Distanz d hat höchstens 2^d Spiele) verhindert,
 * dass beim FA-Cup eine frühe Runde vor der Final-Auslosung fälschlich
 * "Finale"/"Halbfinale" heißt.
 */
export function matchStageLabel(
  match: Pick<OpMatch, 'round' | 'group' | 'roundNumber' | 'matchNumber'>,
  allMatches?: ReadonlyArray<Pick<OpMatch, 'round' | 'roundNumber'>> | null,
): string {
  if (match.round === 'GROUP') {
    if (typeof match.matchNumber === 'number') {
      return t('web.service.tournament-op-service.gruppe-spiel', { value: match.group ?? '?', matchNumber: match.matchNumber });
    }
    return matchRoundLabel(match);
  }
  if (typeof match.roundNumber === 'number' && Array.isArray(allMatches)) {
    const bracketRounds = allMatches
      .filter((m) => m.round !== 'GROUP' && typeof m.roundNumber === 'number')
      .map((m) => m.roundNumber);
    if (bracketRounds.length > 0) {
      const distance = Math.max(...bracketRounds) - match.roundNumber;
      const matchesInRound = bracketRounds.filter((r) => r === match.roundNumber).length;
      if (
        distance >= 0
        && distance < KO_STAGE_BY_DISTANCE.length
        && matchesInRound <= 2 ** distance
      ) {
        return KO_STAGE_BY_DISTANCE[distance];
      }
    }
  }
  return matchRoundLabel(match);
}

// ---------- Dashboard (User-Auth via dc-sign) ----------

async function createOperation(tournamentId: string): Promise<Operation> {
  const res = await api.post('/api/tournament-op', { tournamentId });
  return res.data;
}

async function getByTournament(tournamentId: string): Promise<OperationData> {
  const res = await api.get(`/api/tournament-op/by-tournament/${tournamentId}`);
  return res.data;
}

async function updateConfig(
  operationId: string,
  payload: {
    gameConfig?: Partial<GameConfig>;
    settings?: OpSettings;
    stageType?: StageType;
    groupConfig?: Partial<GroupConfig>;
  },
): Promise<Operation> {
  const res = await api.post(`/api/tournament-op/${operationId}/config`, payload);
  return res.data;
}

async function importParticipants(operationId: string): Promise<{ imported: number; participants: Participant[] }> {
  const res = await api.post(`/api/tournament-op/${operationId}/participants/import`);
  return res.data;
}

async function addParticipant(operationId: string, name: string): Promise<Participant> {
  const res = await api.post(`/api/tournament-op/${operationId}/participants`, { name });
  return res.data;
}

async function removeParticipant(operationId: string, participantId: string): Promise<void> {
  await api.post(`/api/tournament-op/${operationId}/participants/remove`, { participantId });
}

async function createBoard(operationId: string, name: string): Promise<Board> {
  const res = await api.post(`/api/tournament-op/${operationId}/boards`, { name });
  return res.data;
}

async function removeBoard(operationId: string, boardId: string): Promise<void> {
  await api.post(`/api/tournament-op/${operationId}/boards/remove`, { boardId });
}

/**
 * Board-Konfiguration setzen: feste Gruppen-Bindung ({ fixedGroup }) oder Pause
 * ({ paused }). Nur `paused` senden lässt fixedGroup im Backend unverändert.
 * Defensiv: Response wird nur als Board zurückgegeben, wenn sie wie eines aussieht
 * (ältere Backends kennen den Endpoint nicht bzw. liefern andere Bodies).
 */
async function setBoardConfig(
  operationId: string,
  boardId: string,
  config: { fixedGroup?: string | null; paused?: boolean },
): Promise<Board | null> {
  const res = await api.post(`/api/tournament-op/${operationId}/boards/${boardId}/config`, config);
  const body = res.data;
  if (body && typeof body === 'object' && typeof (body as any).id === 'string') {
    return body as Board;
  }
  return null;
}

/** Board pausieren/fortsetzen (pausiert = keine Zuweisung) — sendet NUR `paused`. */
async function setBoardPaused(operationId: string, boardId: string, paused: boolean): Promise<Board | null> {
  return setBoardConfig(operationId, boardId, { paused });
}

// ---------- Turnierleitung im laufenden Betrieb (27.09.2026) ----------

/**
 * Spieler streichen. ANNUL = alle Ergebnisse annulliert (Tabelle ohne ihn),
 * FORFEIT = gespielte Ergebnisse bleiben, offene Spiele kampflos verloren.
 */
async function withdrawParticipant(
  operationId: string,
  participantId: string,
  mode: WithdrawMode,
): Promise<{ participant: Participant; affectedMatches: number }> {
  const res = await api.post(`/api/tournament-op/${operationId}/participants/withdraw`, { participantId, mode });
  return res.data;
}

/** Spieler nachmelden (nur GROUP_KO, solange die KO-Phase nicht ausgelost ist). */
async function lateEntry(
  operationId: string,
  name: string,
  group: string,
): Promise<{ participant: Participant; matchCount: number }> {
  const res = await api.post(`/api/tournament-op/${operationId}/participants/late`, { name, group });
  return res.data;
}

/** Ergebnis eintragen (offenes Spiel) bzw. korrigieren (beendetes Spiel). Genau eine Seite erreicht die Siegzahl. */
async function setManualResult(operationId: string, matchId: string, legsWon: [number, number]): Promise<OpMatch> {
  const res = await api.post(`/api/tournament-op/${operationId}/matches/${matchId}/result`, { legsWon });
  return res.data;
}

/** Spiel neu ansetzen (ASSIGNED/LIVE): Stand gelöscht, Board frei, wieder READY. */
async function resetMatch(operationId: string, matchId: string): Promise<OpMatch> {
  const res = await api.post(`/api/tournament-op/${operationId}/matches/${matchId}/reset`);
  return res.data;
}

/** Spiel auf ein anderes freies, nicht pausiertes Board (Stand bleibt). */
async function moveMatch(operationId: string, matchId: string, boardId: string): Promise<OpMatch> {
  const res = await api.post(`/api/tournament-op/${operationId}/matches/${matchId}/move`, { boardId });
  return res.data;
}

/** Auslosung zurücksetzen (nur solange kein Spiel begonnen hat; sonst 400 mit Text). */
async function resetDraw(operationId: string): Promise<Operation> {
  const res = await api.post(`/api/tournament-op/${operationId}/reset-draw`);
  return res.data;
}

/** Turnier beenden: offene Spiele werden VOID, Boards frei; optional Sieger. */
async function finishOperation(operationId: string, winnerParticipant: string | null): Promise<Operation> {
  const res = await api.post(`/api/tournament-op/${operationId}/finish`, { winnerParticipant });
  return res.data;
}

/** KO-Baum + Spielliste (öffentlich, kein Auth). null, wenn das Backend den Endpoint nicht kennt. */
async function getBracket(operationId: string): Promise<BracketState | null> {
  const res = await axios.get(`${API_BASE_URL}/api/tournament-op/${operationId}/bracket`);
  return parseBracketState(res.data);
}

async function startDraw(operationId: string): Promise<{ ok: boolean; matchCount: number }> {
  const res = await api.post(`/api/tournament-op/${operationId}/draw`);
  return res.data;
}

async function assignMatch(operationId: string, matchId: string, boardId: string): Promise<void> {
  await api.post(`/api/tournament-op/${operationId}/assign`, { matchId, boardId });
}

/** Ergebnis freigeben (nur bei settings.resultApproval): Match → APPROVED + Weiterschaltung. */
async function approveResult(operationId: string, matchId: string): Promise<void> {
  await api.post(`/api/tournament-op/${operationId}/approve`, { matchId });
}

/** Ergebnis verwerfen: Match zurück auf READY mit leeren Legs (nur bei Status FINISHED). */
async function reopenMatch(operationId: string, matchId: string): Promise<void> {
  await api.post(`/api/tournament-op/${operationId}/reopen`, { matchId });
}

/** Eigene, nicht beendete Turnierbetriebe (RUNNING zuerst, dann neueste). */
async function getMyOperations(): Promise<MyOperationEntry[]> {
  const res = await api.get('/api/tournament-op/mine');
  return Array.isArray(res.data) ? res.data : [];
}

/** Öffentlicher Live-Zustand (kein Auth). */
async function getLiveState(operationId: string): Promise<OperationData> {
  const res = await axios.get(`${API_BASE_URL}/api/tournament-op/${operationId}/live`);
  return res.data;
}

/** Öffentliche Liste aller laufenden Turniere (kein Auth). */
async function getLiveList(): Promise<LiveListEntry[]> {
  const res = await axios.get(`${API_BASE_URL}/api/tournament-op/live-list`);
  return Array.isArray(res.data) ? res.data : [];
}

/** Öffentliches Einzelspiel mit Namen, Board und Stats (kein Auth). */
async function getPublicMatch(operationId: string, matchId: string): Promise<PublicMatchResponse> {
  const res = await axios.get(`${API_BASE_URL}/api/tournament-op/${operationId}/match/${matchId}`);
  return res.data;
}

// ---------- Board (Scorer-Gerät, Header dc-board) ----------

/** Zeitlimit für Board-Requests (Scorer-Gerät). Ohne Limit bleibt bei einem hängenden Request
 *  (Pub-WLAN, Funkloch) `sending` für immer true: Ziffern werden noch angenommen, Enter aber
 *  still ignoriert – erst ein Reload hilft (beobachtet in der UI-Simulation 27.09.2026). Mit
 *  Limit kommt nach 15 s ein Fehler-Banner und die Eingabe geht weiter. */
export const BOARD_REQUEST_TIMEOUT_MS = 15000;

export const boardApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: BOARD_REQUEST_TIMEOUT_MS,
});

boardApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(BOARD_TOKEN_KEY);
  if (token) {
    config.headers['dc-board'] = token;
  }
  return config;
});

function getBoardToken(): string | null {
  return localStorage.getItem(BOARD_TOKEN_KEY);
}

function setBoardToken(token: string): void {
  localStorage.setItem(BOARD_TOKEN_KEY, token);
}

function clearBoardToken(): void {
  localStorage.removeItem(BOARD_TOKEN_KEY);
}

async function boardLogin(code: string): Promise<BoardLoginResponse> {
  const res = await boardApi.post('/api/tournament-op/board/login', { code });
  return res.data;
}

async function boardState(): Promise<BoardStateResponse> {
  const res = await boardApi.get('/api/tournament-op/board/state');
  return res.data;
}

async function boardStartMatch(firstLegStarter: 0 | 1): Promise<{ match: OpMatch }> {
  const res = await boardApi.post('/api/tournament-op/board/start', { firstLegStarter });
  return res.data;
}

async function boardEnterVisit(
  playerIdx: number,
  score: number,
  dartsUsed?: number,
): Promise<{ result: VisitResult; match: OpMatch }> {
  const body: { playerIdx: number; score: number; dartsUsed?: number } = { playerIdx, score };
  if (dartsUsed !== undefined) body.dartsUsed = dartsUsed;
  const res = await boardApi.post('/api/tournament-op/board/visit', body);
  return res.data;
}

async function boardUndoVisit(): Promise<{ match: OpMatch }> {
  const res = await boardApi.post('/api/tournament-op/board/undo');
  return res.data;
}

/** Fehlertext aus einer axios-Fehler-Response ziehen (Backend sendet Text-Bodies). */
function errorText(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === 'string' && data.trim() !== '') return data;
    if (data && typeof (data as any).message === 'string') return (data as any).message;
  }
  return fallback;
}

const tournamentOpService = {
  createOperation,
  getByTournament,
  updateConfig,
  importParticipants,
  addParticipant,
  removeParticipant,
  createBoard,
  removeBoard,
  setBoardConfig,
  setBoardPaused,
  withdrawParticipant,
  lateEntry,
  setManualResult,
  resetMatch,
  moveMatch,
  resetDraw,
  finishOperation,
  getBracket,
  startDraw,
  assignMatch,
  approveResult,
  reopenMatch,
  getMyOperations,
  getLiveState,
  getLiveList,
  getPublicMatch,
  getBoardToken,
  setBoardToken,
  clearBoardToken,
  boardLogin,
  boardState,
  boardStartMatch,
  boardEnterVisit,
  boardUndoVisit,
  errorText,
};

export default tournamentOpService;
